import PictureAsPdf from '@mui/icons-material/PictureAsPdf';
import { Button } from '@src/common/components/button/Button';
import { LoadingSpinner } from '@src/common/components/LoadingSpinner';
import { Stack } from '@src/common/components/Stack';
import { ToastLevel } from '@src/common/components/ToastLevel';
import { useToast } from '@src/common/hooks/useToast';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { getLastPartOfPath } from '@src/documents/hooks/useFindAllDocumentsForOrder';
import type {
  Document,
  PermitApplication,
} from '@src/orders/domain/PermitApplication';
import type { CompletePermitSchemaType } from '@src/permits/completePermit/fields/CompletePermitFields';
import { StatePermitDocumentFields } from '@src/permits/completePermit/fields/CompletePermitFields';
import {
  EffectiveDateField,
  ExpirationDateField,
  StatePermitNumberField,
} from '@src/permits/completePermit/fields/CompletePermitFields';
import type { StatePermitFormType } from '@src/permits/completePermit/StatePermitDropzone';
import { useGenerateSelfIssuedPdf } from '@src/permits/hooks/useGenerateSelfIssuedPdf';
import type { Control, UseFormGetValues } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import type { UseFormSetValue } from 'react-hook-form/dist/types/form';
import { useTranslation } from 'react-i18next';

const generatePDFComponentHeight = 40;
const button = css({
  padding: '0 18px',
  height: `${generatePDFComponentHeight}px`,
  gap: '6px',
});
export const GenerateSelfIssuedPdfButton = ({
  getValues,
  control,
  permitApplication,
  isSelfIssued,
  readOnly,
  setValue,
}: {
  setValue: UseFormSetValue<StatePermitFormType>;
  getValues: UseFormGetValues<CompletePermitSchemaType>;
  permitApplication: PermitApplication;
  control: Control<CompletePermitSchemaType & StatePermitFormType>;
  readOnly?: boolean;
  isSelfIssued?: boolean;
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [effectiveDateValue, statePermitNumberValue] = useWatch({
    control,
    name: [EffectiveDateField.fieldName, StatePermitNumberField.fieldName],
  });

  const { generateSelfIssuedPdf, isPDFGenerationPending } =
    useGenerateSelfIssuedPdf({
      permitApplicationId: permitApplication.id,
      onSuccess: (newDocument: Document) => {
        const uploadFile = {
          documentId: newDocument.documentId,
          name: getLastPartOfPath(newDocument.key),
          sendToClient: newDocument.sendToClient,
          displayName: newDocument.displayName,
        };
        setValue(StatePermitDocumentFields.fieldName, uploadFile, {
          shouldValidate: true,
        });
        showToast({
          severity: ToastLevel.success,
          message: t('selfIssued.generated'),
        });
      },
      onError: () => {
        showToast({
          severity: ToastLevel.error,
          message: t('selfIssued.error'),
        });
      },
    });

  const isGeneratePdfButtonDisabled =
    !effectiveDateValue || !statePermitNumberValue || isPDFGenerationPending;

  const generatePdf = async () => {
    generateSelfIssuedPdf({
      expirationDate: getValues(ExpirationDateField.fieldName)!,
      effectiveDate: getValues(EffectiveDateField.fieldName)!,
      statePermitNumber: getValues(StatePermitNumberField.fieldName)!,
    });
  };

  if (!isSelfIssued) {
    return;
  }

  return (
    <Stack direction="row" gap={theme.spacing['3']}>
      <Button
        type="button"
        className={button}
        variant="outlined"
        onClick={generatePdf}
        disabled={readOnly || isGeneratePdfButtonDisabled}
        aria-labelledby={t('selfIssued.generatePdf')}
      >
        <PictureAsPdf />
        <div id={t('selfIssued.generatePdf')}>
          {t('selfIssued.generatePdf')}
        </div>
        {isPDFGenerationPending && (
          <LoadingSpinner sizeInPx={generatePDFComponentHeight / 1.5} />
        )}
      </Button>
    </Stack>
  );
};
